// Runtime i18n engine (WEBSITE-BEST-PRACTICES.md §6).
// - Inline-first: reads window.__INLINE_TRANSLATIONS__ before any network fetch (LCP, §6.4).
// - Applies data-translate / -attr / -placeholder / -alt attributes.
// - Supports dot notation and array indexes ("items[0].title").
// - No silent fallback: a missing key leaves the node empty and logs an error (§6.2).

import { qsa } from '/shared/utils/core/dom.js';
import { resolveKey, isHtmlValue } from '/shared/utils/core/i18nKeys.js';

export default class I18nCore {
  /**
   * @param {{ lang: string }} options
   */
  constructor({ lang }) {
    this.lang = lang;
    this.cache = new Map();
    // A namespace being fetched, so two sections asking for it at once share one request.
    this.loading = new Map();
    this.inline = (typeof window !== 'undefined' && window.__INLINE_TRANSLATIONS__) || {};
  }

  /**
   * Load a namespace dictionary for the active language (inline-first, then fetch).
   * @param {string} namespace e.g. "home/hero"
   * @returns {Promise<object>}
   */
  async load(namespace) {
    if (this.cache.has(namespace)) return this.cache.get(namespace);

    const inline = this.inline?.[this.lang]?.[namespace];
    if (inline) {
      this.cache.set(namespace, inline);
      return inline;
    }

    if (!this.loading.has(namespace)) {
      this.loading.set(
        namespace,
        this.#fetch(namespace).finally(() => this.loading.delete(namespace))
      );
    }
    return this.loading.get(namespace);
  }

  /** One network read of a namespace; the result lands in the cache, failures as {}. */
  async #fetch(namespace) {
    try {
      const res = await fetch(`/i18n/${namespace}/${this.lang}.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const dict = await res.json();
      this.cache.set(namespace, dict);
      return dict;
    } catch (err) {
      console.error(`[i18n] failed to load namespace "${namespace}" (${this.lang}):`, err.message);
      this.cache.set(namespace, {});
      return {};
    }
  }

  /**
   * Translate every data-translate* node within a root (the root itself included).
   * Each node resolves its namespace from the nearest ancestor data-translate-section.
   * @param {Element|Document} root
   * @returns {Promise<void>}
   */
  async apply(root) {
    if (!root) return;
    const scope = root.nodeType === 9 ? root.documentElement : root;
    const nodes = this.#collectNodes(scope);

    // Preload every distinct namespace in parallel.
    const namespaces = new Set(nodes.map((n) => n.namespace).filter(Boolean));
    await Promise.all([...namespaces].map((ns) => this.load(ns)));

    for (const { el, namespace } of nodes) {
      if (!namespace) continue;
      const dict = this.cache.get(namespace) || {};
      this.#applyNode(el, dict, namespace);
    }
  }

  /** Collect candidate nodes with their resolved namespace. @returns {{el: Element, namespace: string|null}[]} */
  #collectNodes(scope) {
    const selector =
      '[data-translate], [data-translate-attr], [data-translate-placeholder], [data-translate-alt]';
    const list = qsa(selector, scope);
    if (scope.matches && scope.matches(selector)) list.unshift(scope);
    return list.map((el) => ({ el, namespace: this.#resolveNamespace(el) }));
  }

  /** Find the nearest data-translate-section ancestor (or self). */
  #resolveNamespace(el) {
    const holder = el.closest('[data-translate-section]');
    return holder ? holder.getAttribute('data-translate-section') : null;
  }

  /** Apply all translation attributes of a single element. */
  #applyNode(el, dict, namespace) {
    const textKey = el.getAttribute('data-translate');
    if (textKey !== null) {
      const value = resolveKey(dict, textKey);
      if (typeof value !== 'string') this.#miss(namespace, textKey);
      else if (isHtmlValue(value)) el.innerHTML = value;
      else el.textContent = value;
    }

    const attrSpec = el.getAttribute('data-translate-attr');
    if (attrSpec) this.#applyAttrSpec(el, dict, namespace, attrSpec);

    const placeholderKey = el.getAttribute('data-translate-placeholder');
    if (placeholderKey) this.#applySimple(el, dict, namespace, placeholderKey, 'placeholder');

    const altKey = el.getAttribute('data-translate-alt');
    if (altKey) this.#applySimple(el, dict, namespace, altKey, 'alt');
  }

  /** "attr:key, attr2:key2" → setAttribute for each pair. */
  #applyAttrSpec(el, dict, namespace, spec) {
    for (const pair of spec.split(',')) {
      const [attr, key] = pair.split(':').map((s) => s.trim());
      if (!attr || !key) continue;
      const value = resolveKey(dict, key);
      if (typeof value !== 'string') this.#miss(namespace, key);
      else el.setAttribute(attr, value);
    }
  }

  /** Fill a single property (placeholder/alt) from a key. */
  #applySimple(el, dict, namespace, key, prop) {
    const value = resolveKey(dict, key);
    if (typeof value !== 'string') this.#miss(namespace, key);
    else el[prop] = value;
  }

  /** Log a missing key (no silent fallback). */
  #miss(namespace, key) {
    console.error(`[i18n] missing key "${key}" in namespace "${namespace}" (${this.lang})`);
  }
}
